import React from 'react';
import { useTranslation } from 'react-i18next';
import { SPRINT_MANAGEMENT_CONSTANTS } from '../../../../shared/appConstants';
import { COLORS } from '../../../../shared/colors';
import velocityIcon from '../../../../assets/images/velocity_icon.svg';
import sprintGoalIcon from '../../../../assets/images/sprint_goal.svg';
import sprintGoalBg from '../../../../assets/images/sprint_goal_bg.png';
import OverflowTooltip from '../../../../shared/OverflowTooltip';
import { Box } from '@mui/material';

const SprintHealthCard = ({ sprintId, apiData }) => {
  const { t } = useTranslation();

  const details = apiData?.data?.details || {};
  const health = details.health || {};
  const goalText = typeof details.goal === 'string' ? details.goal.trim() : (details.goal || '-');

  const isExecutionReady = health.execution_ready !== false;
  const healthScore = health.score;
  const normalizedHealthScore = healthScore == null ? 0 : Math.min(100, Math.max(0, healthScore));
  const progressPercent = health.progress;
  const displayMetric = (value, suffix = '') => value == null ? 'N/A' : `${value}${suffix}`;
  
  const sprintStatus = health.state || t('sprintManagement.onTrack');
  const isAtRisk = health.state === SPRINT_MANAGEMENT_CONSTANTS.API_STATE.AT_RISK;
  const isDraft = health.state === SPRINT_MANAGEMENT_CONSTANTS.API_STATE.DRAFT;

  const riskLevel = health.risk || (isDraft || !isExecutionReady ? t('sprintManagement.none') : SPRINT_MANAGEMENT_CONSTANTS.RISK_LEVELS.NOT_APPLICABLE);
  const velocity = health.velocity;
  const blockedTickets = health.blocked_ticket_count == null ? 0 : health.blocked_ticket_count;

  const getRiskColor = (risk) => {
    const r = (risk || '').toLowerCase();
    if (r.includes(SPRINT_MANAGEMENT_CONSTANTS.RISK_LEVELS.HIGH.toLowerCase())) return COLORS.priority.high;
    if (r.includes(SPRINT_MANAGEMENT_CONSTANTS.RISK_LEVELS.MEDIUM.toLowerCase())) return COLORS.priority.medium;
    if (r.includes(SPRINT_MANAGEMENT_CONSTANTS.RISK_LEVELS.LOW.toLowerCase())) return COLORS.priority.low;
    return COLORS.gray[500];
  };

  const prediction = !isExecutionReady
    ? t('sprintManagement.notEnoughData')
    : isDraft
      ? t('sprintManagement.notStarted')
      : isAtRisk
        ? t('sprintManagement.completionAtRisk')
        : t('sprintManagement.likelyToComplete');

  return (
    <div className="sprint-health-container">
      <div 
        className="sm-card goal-card"
        style={{
          backgroundImage: `linear-gradient(rgba(255, 255, 255, 0.8), rgba(255, 255, 255, 0.8)), url(${sprintGoalBg})`,
          backgroundSize: '100% 100%',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="sm-card-header-with-icon">
          <img src={sprintGoalIcon} alt="" className="header-icon" />
          <h2 className="card-title">{t('sprintManagement.goalTitle')}</h2>
        </div>
        <OverflowTooltip 
          title={goalText} 
          component={Box}
          className="goal-text" 
          placement="bottom-start" 
          tooltipWidth={400} 
          maxLines={6}
        >
          {goalText}
        </OverflowTooltip>
      </div>

      <div className="sm-card health-card">
        <div className="health-content">
          <div className="circular-progress-section">
            <h2 className="card-title">{t('sprintManagement.healthTitle')}</h2>
            <div className="circular-progress">
              <svg viewBox="0 0 100 100">
                <circle className="bg-circle" cx="50" cy="50" r="45" />
                <circle className="progress-circle" cx="50" cy="50" r="45" 
                  strokeDasharray="283" 
                  strokeDashoffset={283 - (283 * normalizedHealthScore) / 100}
                />
              </svg>
              <div className="progress-text">
                {healthScore != null ? (
                  <>
                    <span className="completed">{healthScore}</span>
                    <span className="committed">{t('sprintManagement.healthScoreMax')}</span>
                  </>
                ) : (
                  <span className="completed na">N/A</span>
                )}
              </div>
            </div>
            <div className={`status-badge ${isDraft ? SPRINT_MANAGEMENT_CONSTANTS.HEALTH_STATUS.DRAFT : isAtRisk ? SPRINT_MANAGEMENT_CONSTANTS.HEALTH_STATUS.AT_RISK : SPRINT_MANAGEMENT_CONSTANTS.HEALTH_STATUS.ON_TRACK}`}>
              {sprintStatus}
            </div>
          </div>

          <div className="health-metrics-grid">
            <div className="metric-box">
              <div className="metric-header">
                <span className="metric-title">{t('sprintManagement.progress')}</span>
                <span className="metric-value">{displayMetric(progressPercent ?? 0, '%')}</span>
              </div>
              <div className="linear-progress-bar">
                <div className="fill" style={{ '--progress-width': `${progressPercent || 0}%` }} />
              </div>
            </div>
            <div className="metric-box">
              <div className="metric-header">
                <span className="metric-title">{t('sprintManagement.velocity')}</span>
                <span className="metric-value">{displayMetric(velocity)}{velocity != null && <span className="unit-val">SP</span>}</span>
              </div>
            </div>
            <div className="metric-box risk-level-box">
              <div className="metric-header">
                <span className="metric-title">{t('sprintManagement.riskLevel')}</span>
              </div>
              <div className="metric-footer">
                <span 
                  className={`metric-value risk-${riskLevel.toLowerCase().replace(/\s+/g, '-')}`}
                  style={{ color: getRiskColor(riskLevel) }}
                >
                  <span className="circle-indicator"></span>
                  {riskLevel}
                </span>
              </div>
            </div>

            <div className="metric-box blocked-box">
              <div className="metric-header blocked-header">
                <span className="metric-title">{t('sprintManagement.blocked')}</span>
                <div className="blocked-content">
                  <div className="blocked-value">{displayMetric(blockedTickets)}</div>
                  {blockedTickets != null && <div className="blocked-unit">{t('sprintManagement.tickets')}</div>}
                </div>
              </div>
            </div>
            <div className="metric-box prediction-box">
              <div className="metric-header prediction-header">
                <span className="metric-title">{t('sprintManagement.prediction')}</span>
                <img src={velocityIcon} alt="prediction icon" className={`prediction-icon ${isDraft ? 'icon-draft' : isAtRisk ? 'icon-risk' : 'icon-safe'}`} />
              </div>
              <div className={`prediction-text ${isDraft ? 'text-draft' : isAtRisk ? 'text-risk' : 'text-safe'}`}>
                {prediction}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SprintHealthCard;
